import { Controller } from "@hotwired/stimulus"

// Form-driven person picker: wraps the standalone `person-picker` component
// and binds it to a hidden input + a visual chip. The user picks a person in
// the search modal; we capture the `person-picker:choose` event bubbling up
// from the nested picker, write the id into the hidden input and re-render
// the chip.
//
// Targets:
//   input       - the hidden <input> that the form submits
//   chip        - the container that shows the currently selected person as a chip
//   triggerLabel - the picker-trigger button's label span (so we can swap
//                  "Selecionar pessoa" ↔ "Trocar pessoa")
//
// Values:
//   triggerLabelEmpty   - label when no person is selected
//   triggerLabelFilled  - label when a person is selected
//
// Events listened (declared in the markup via data-action):
//   person-picker:choose -> select
export default class extends Controller {
  static targets = ["input", "chip", "triggerLabel"]
  static values = {
    triggerLabelEmpty: String,
    triggerLabelFilled: String
  }

  select(event) {
    const { personId, personName } = event.detail || {}
    if (!personId) return

    this.inputTarget.value = personId
    if (this.hasChipTarget) {
      this.chipTarget.innerHTML = this._renderChip(personId, personName)
    }
    this._refreshTriggerLabel()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clear(event) {
    event?.preventDefault()
    this.inputTarget.value = ""
    if (this.hasChipTarget) this.chipTarget.innerHTML = ""
    this._refreshTriggerLabel()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _refreshTriggerLabel() {
    if (!this.hasTriggerLabelTarget) return
    const filled = this.inputTarget.value.trim().length > 0
    const label = filled ? this.triggerLabelFilledValue : this.triggerLabelEmptyValue
    if (label) this.triggerLabelTarget.textContent = label
  }

  _renderChip(personId, personName) {
    const initial = (personName || "?").trim().charAt(0).toUpperCase()
    const name    = this._escape(personName || "")
    return `
      <span class="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white py-1 pl-1 pr-2 text-xs text-zinc-700 shadow-sm">
        <span class="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-100 text-[10px] font-semibold text-zinc-500">${this._escape(initial)}</span>
        <span class="max-w-[14rem] truncate font-medium text-zinc-800">${name}</span>
        <button type="button" data-action="click->person-field-picker#clear"
                class="ml-1 flex h-5 w-5 items-center justify-center rounded-full text-zinc-400 hover:bg-red-50 hover:text-red-600"
                title="Remover">
          <span aria-hidden="true">×</span>
        </button>
      </span>
    `
  }

  _escape(text) {
    const div = document.createElement("div")
    div.textContent = text == null ? "" : String(text)
    return div.innerHTML
  }
}
